import { useHistory } from 'react-router-dom';
import {
  CheckCircle,
  Delete,
  Home,
  RemoveRedEye,
  SaveAlt,
} from '@mui/icons-material';
import { createCommitmentViewRoute } from 'adminConstants/routes';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { SortOrder } from 'further-types/utils';
import Menu from 'further-ui/components/Menu';
import SearchField from 'further-ui/components/SearchField';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  DateRange,
  getDateRange,
  numberToCurrencyString,
  downloadFileFromTrpc,
} from 'further-ui/utils';

import api from 'lib/trpcClient';
import Divider from '@mui/material/Divider';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { Typography } from '@mui/material';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import AvatarWithName from 'components/new/AvatarWithName';
import DownloadPcaps from './DownloadPcaps';

const pageId = 'commitments';

const ListCommitments: React.FC = () => {
  const history = useHistory();
  const pagination = usePagination({
    orderBy: 'commitmentDate',
    order: SortOrder.Desc,
    id: pageId,
  });

  const [hasDeletePermission, canImpersonateInvestor] = useHasPermissions([
    RolePermission.LpDeleteCommitment,
    RolePermission.UseImpersonationAccessInvestor,
  ]);
  const deleteCommitmentDisclosure = useDisclosure<{ id: string }>();
  const pcapDrawer = useDisclosure();
  const { filters, handleChangeFilters } = useFilters<{
    investorName: string;
    dateRange?: DateRange;
  }>(pageId, {
    defaultFilters: {
      investorName: '',
      dateRange: undefined,
    },
    onFiltersChange: pagination.toFirstPage,
  });

  const { selectedVehicleId, vehicle } = useLayoutContext();

  const [lpFirm] = api.lpFirm.getCurrentFirm.useSuspenseQuery();

  const commitmentsQuery = {
    vehicleId: selectedVehicleId ?? '',
    ...pagination.properties,
    ...filters,
    ...getDateRange(filters.dateRange),
  };

  const { data, isFetching } = api.commitments.list.useQuery(commitmentsQuery);
  const commitments = data?.results ?? [];

  const { mutateAsync: runExport, ...exportMutation } =
    api.commitments.exportList.useMutation(
      useHandleTrpcMutation()({
        invalidations: [],
      }),
    );

  const { mutate: runDelete } = api.commitments.delete.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Commitment deleted successfully.',
    }),
  );
  const launchPortalImpersonation = useLaunchPortalImpersonation();

  const columns: Array<TableColumn<(typeof commitments)[number]>> = [
    {
      label: 'Investor',
      key: 'investorName',
      width: 250,
      render: (commitment) => (
        <AvatarWithName
          name={commitment.investorName}
          avatarSize="lg"
          subtext={commitment.commitmentDate?.toReadable()}
        />
      ),
    },
    {
      label: 'Commitment',
      key: 'totalCommitmentLcy',
      width: 150,
      render: (commitment) => {
        if (
          commitment.isCarryPartnerCommitment &&
          vehicle?.carrySettings?.capitalContributionPercentage !== null &&
          !vehicle?.firstCloseDate
        ) {
          return 'TBC';
        }
        return numberToCurrencyString(commitment.totalCommitmentLcy, {
          currency: vehicle?.baseCurrency,
        });
      },
    },
    {
      label: 'Paid-in capital',
      key: 'paidInCapital',
      width: 150,
      render: (commitment) => {
        return numberToCurrencyString(commitment.paidInCapitalLcy, {
          currency: vehicle?.baseCurrency,
        });
      },
    },
    {
      label: 'Total value',
      key: 'totalValue',
      width: 150,
      render: (commitment) => {
        return numberToCurrencyString(commitment.totalValueLcy, {
          currency: vehicle?.baseCurrency,
        });
      },
    },
    {
      label: 'Distributions',
      key: 'distributions',
      width: 150,
      render: (commitment) => {
        return numberToCurrencyString(commitment.distributionsTotalLcy, {
          currency: vehicle?.baseCurrency,
        });
      },
    },
  ];

  return (
    <Page
      tabTitle="Commitments"
      heading="Commitments"
      spacing="lg"
      headingIcon={CheckCircle}
      customActions={
        <Menu
          label="Export"
          buttonVariant="outlined"
          loading={exportMutation.isPending}
          alignWithIconRightEdge
          gap="xs"
          items={[
            {
              label: 'Table export',
              icon: SaveAlt,
              onClick: async () => {
                const data = await runExport(commitmentsQuery);
                downloadFileFromTrpc(data);
              },
            },
            {
              label: 'PCAP export',
              icon: SaveAlt,
              onClick: pcapDrawer.onOpen,
            },
          ]}
        />
      }
    >
      <Divider />
      <PageFilters>
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            name="investorName"
            placeholder="Search by investor"
            fullWidth
            value={filters.investorName}
            onSearch={(investorName) => {
              handleChangeFilters({ investorName });
            }}
            allowEmptyQuery
          />
        </Filter>
        <Filter size="md">
          <MuiDateRangePicker
            dateRange={filters.dateRange}
            onDateRangeChange={(dateRange) => {
              handleChangeFilters({ dateRange });
            }}
            placeholder="Select commitment date range"
          />
        </Filter>
      </PageFilters>
      <Table
        columns={columns}
        rows={commitments}
        isLoading={isFetching}
        rowsTotalCount={data?.totalCount ?? 0}
        scrollable
        pagination={pagination}
        emptyMessage="No commitments found"
        disableSorting
        onRowClick={(commitment) => {
          history.push(
            createCommitmentViewRoute(commitment.investorId, commitment.id),
          );
        }}
        actions={[
          {
            label: 'View commitment',
            icon: RemoveRedEye,
            link: (commitment) =>
              createCommitmentViewRoute(commitment.investorId, commitment.id),
          },
          {
            label: 'View portal',
            icon: Home,
            hidden: !canImpersonateInvestor,
            onClick: (commitment) => {
              if (!lpFirm?.portalSubdomain) return;
              launchPortalImpersonation.investor({
                investorId: commitment.investorId,
                firmDomain: lpFirm.portalSubdomain,
              });
            },
          },
          {
            hidden: (commitment) =>
              !hasDeletePermission || !commitment.canDelete,
            label: 'Delete commitment',
            icon: Delete,
            onClick: (commitment) => {
              deleteCommitmentDisclosure.stageAction({ id: commitment.id });
              deleteCommitmentDisclosure.onOpen();
            },
          },
        ]}
      />
      {pcapDrawer.isOpen && selectedVehicleId && (
        <DownloadPcaps
          vehicleId={selectedVehicleId}
          open={pcapDrawer.isOpen}
          onClose={pcapDrawer.onClose}
        />
      )}
      <ConfirmDialog
        maxWidth="sm"
        open={deleteCommitmentDisclosure.isOpen}
        onClose={deleteCommitmentDisclosure.onClose}
        title="Delete commitment"
        content={
          <Typography variant="body2">
            Are you sure you want to delete this commitment? Any documents
            signed will still be available on the <em>Document signing</em> page
            within <em>Onboarding</em>.
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Delete',
            color: 'error',
            onClick: () => {
              deleteCommitmentDisclosure.onClose();
              if (!vehicle) return;
              runDelete({
                commitmentId: deleteCommitmentDisclosure.actionProps.id,
                vehicleId: vehicle._id ?? '',
              });
            },
          },
        }}
      />
    </Page>
  );
};

export default ListCommitments;
